import { CdkTextareaAutosize, TextFieldModule } from '@angular/cdk/text-field';
import {
    afterNextRender,
    Component,
    EventEmitter,
    inject,
    Injector,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

@Component({
    selector: 'app-details-text-area',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatInputModule, TextFieldModule, MatIconModule],
    templateUrl: './details-text-area.component.html',
    styleUrl: './details-text-area.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class DetailsTextAreaComponent implements OnChanges {
    private readonly _injector = inject(Injector);

    @Input() areaControl: FormControl;
    @Input() errorMessage?: string;
    @ViewChild('autosize') autosize: CdkTextareaAutosize;
    @Output() areaBlur = new EventEmitter<FocusEvent>();

    icon = new BehaviorSubject<string>('');
    isDisabled = false;
    isEditing = false;
    isHovering = false;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['areaControl']) {
            this.areaControl = changes['areaControl'].currentValue || new FormControl('');
            this.updateIcon();
            this.isDisabled = this.areaControl.disabled;

            if (this.areaControl.errors) {
                this.areaControl.markAsTouched();
            }
        }
    }

    triggerResize() {
        // Wait for content to render, then trigger textarea resize.
        afterNextRender(
            () => {
                this.autosize.resizeToFitContent(true);
            },
            {
                injector: this._injector
            }
        );
    }

    onFocus() {
        if (this.isDisabled) {
            return;
        }
        this.isHovering = false;
        this.isEditing = true;
        this.icon.next('');
    }

    onBlur(event: FocusEvent) {
        if (this.isDisabled) {
            return;
        }
        this.isEditing = false;
        this.areaBlur.emit(event);
        this.updateIcon();
    }

    onMouseEnter() {
        this.setHover(true);
    }

    onMouseLeave() {
        this.setHover(false);
    }

    private setHover(isHover: boolean) {
        if (this.isDisabled) {
            return;
        }
        if (!this.isEditing && this.areaControl.valid) {
            this.icon.next(isHover ? 'edit' : '');
            this.isHovering = isHover;
        }
    }

    private updateIcon() {
        if (this.areaControl.invalid) {
            this.icon.next('warning');
        } else {
            this.icon.next('');
        }
    }
}
